import { Col, Row, Modal, Tabs, Tooltip } from 'antd';
import DataTable from '@portal/components/DataTable/DataTable';
import { translate } from '@portal/services/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EyeOutlined, SettingOutlined } from '@ant-design/icons';
import {
  approvalsMultipleTransactions,
  getApprovalTransactions,
} from '@portal/actions/approveTransaction';
import useReduxState from '@portal/hooks/useReduxState';
import { getTransactionName } from '@portal/enum/transactions';
import { DateTime } from 'luxon';
import { validationStatus } from '@portal/utils/transaction';
import { useTheme } from '@material-ui/core';
import {
  TransactionApprovalType,
  TransactionRequestPendingApprovalStatus,
} from '@portal/enum/approvalsType';
import { moneyFormat } from '@portal/utils/format';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ApprovedTransactionDetailModal from '@portal/components/ApprovedTransactionDetailModal/ApprovedTransactionDetailModal';
import {
  ApproveTransaction,
  FailedApproveTransaction,
} from '@portal/models/module';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { Transaction } from '@portal/models/bank';
import RolesType from '@portal/enum/rolesType';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import * as MessageService from '~/services/message';
import { LANGUAGE } from '~/config/env';
import renderStatusWithColor from '@portal/utils/statusRenderer';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import {
  GridColDef,
  GridSelectionModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import truncateTextWithEllipsis from '@portal/utils/truncateTextWithEllipsis';
import AdvancedModalFilters from '@portal/components/AdvancedModalFilters/AdvancedModalFilters';
import {
  AdvancedFilterType,
  TransactionType,
} from '@portal/models/enumerators';
import { queryStringToObject } from '@portal/services/queryString';
import { maskMoney } from '@portal/services/masks';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { getRouteStackPath } from '@portal/config/routes';

import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedDropdown from '@portal/components/AdvancedDropdown/AdvancedDropdown';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

const initialValues: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'desc',
  limit: 10,
  offset: 0,
};

enum TabIndexEnum {
  PENDING_APPROVAL = 1,
  EXECUTED = 2,
  REPROVED = 3,
}

const Approved = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const {
    user: { roles, me },
    account: { balance },
    approved: {
      list,
      transactionExecuted,
      transactionReproved,
      countPendingApproval,
      countExecuted,
      countReproved,
      multipleApprovalResult,
    },
  } = useReduxState();

  const { palette } = useTheme();
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [status, setStatus] = useState<TransactionApprovalType>();
  const [transactionDetailModalVisible, setTransactionDetailModalVisible] =
    useState(false);
  const [advancedFiltersExecuted, setAdvancedFiltersExecuted] =
    useState(initialValues);
  const [advancedFiltersReproved, setAdvancedFiltersReproved] =
    useState(initialValues);
  const [advancedFiltersPendingApproval, setAdvancedFiltersPendingApproval] =
    useState(initialValues);
  const [transactionDetail, setTransactionDetail] =
    useState<ApproveTransaction>();
  const [tabKey, setTabKey] = useState(TabIndexEnum.PENDING_APPROVAL);
  const [selectedRows, setSelectedRows] = useState<ApproveTransaction[]>([]);
  const [cachedSelected, setCachedSelected] = useState<ApproveTransaction[]>(
    []
  );
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [approvalResult, setApprovalResult] = useState<{
    success: ApproveTransaction[];
    failed: FailedApproveTransaction[];
  }>();
  const filterParams = new URLSearchParams(location.search);

  const listExecuted = transactionExecuted?.filter(
    (item) => item.transactionApprovals
  );

  const handleRefresh = (key: number) => {
    if (me.accountId) {
      const newFilter = {
        ...initialValues,
        accountId: me.accountId,
        status: key,
      };
      dispatch(getApprovalTransactions(newFilter));
    }
  };

  const handleCheckboxChange = (selection: GridSelectionModel) => {
    const selectedIDs = new Set((selection ?? []).map((id) => String(id)));
    const selectedRows =
      list?.filter((row) => selectedIDs.has(String(row.id))) || [];
    setSelectedRows(selectedRows);
  };

  const handleConfirmation = async (pin: string) => {
    setIsPinModalVisible(false);
    const encryptedPIN = encrypt(pin);

    setCachedSelected(selectedRows);

    const transactionRequest = selectedRows.map((transaction: Transaction) => ({
      transactionRequestPendingApprovalId: transaction?.id ?? '',
      type: status ?? TransactionApprovalType.REPROVE,
    }));

    const data = {
      authenticate: {
        pin: encryptedPIN,
        timestamp: new Date().getTime().toString(),
      },
      transactionRequest,
    };
    dispatch(
      approvalsMultipleTransactions(data, (err) => {
        if (err instanceof Error) {
          MessageService.error(err.message, { originalError: err });
        } else {
          handleRefresh(tabKey);
          setModalIsVisible(true);
        }
      })
    );
  };

  const openTransactionDetailModal = (transaction) => {
    setTransactionDetail(transaction);
    setTransactionDetailModalVisible(true);
  };

  const columnsPendingApprove = [
    {
      field: 'id',
      flex: 1,
      headerName: 'id',
      sortable: false,
      hide: true,
    },
    {
      field: 'createdAt',
      flex: 1,
      headerName: translate(
        'PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION_DATE'
      ),
      renderCell: (columns) => (
        <>
          {DateTime.fromISO(columns.row.createdAt).toFormat(
            translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
          )}
        </>
      ),
    },
    {
      field: 'solicitation',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION'),
      renderCell: (column: GridRenderCellParams) => (
        <Tooltip title={column.row?.transactionRequest?.account?.name}>
          {truncateTextWithEllipsis(
            column.row?.transactionRequest?.account?.name ?? '-',
            10,
            true
          )}
        </Tooltip>
      ),
    },
    {
      field: 'receiver',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.RECEIVER'),
      renderCell: (column: GridRenderCellParams) => (
        <Tooltip title={column?.row?.transactionRequest?.favored?.alias}>
          {truncateTextWithEllipsis(
            column?.row?.transactionRequest?.favored?.alias ?? '-',
            15,
            true
          )}
        </Tooltip>
      ),
    },
    {
      field: 'agency',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.BANK_AGENCY'),
      renderCell: (column: GridRenderCellParams) => (
        <>{column?.row?.transactionRequest?.favored?.bankAgency ?? '-'}</>
      ),
    },
    {
      field: 'bank_account',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.BANK_ACCOUNT'),
      renderCell: (column: GridRenderCellParams) => (
        <>
          {column?.row?.transactionRequest?.favored?.bankAccount ?? '-'}
          {column?.row?.transactionRequest?.favored?.bankAccountDigit
            ? `-${
                column?.row?.transactionRequest?.favored?.bankAccountDigit ??
                '-'
              }`
            : null}
        </>
      ),
    },
    {
      field: 'transaction',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TYPE'),
      renderCell: (column: GridRenderCellParams) => (
        <Tooltip
          title={getTransactionName(column.row.transactionRequest?.type)}
        >
          {truncateTextWithEllipsis(
            getTransactionName(column.row.transactionRequest?.type) ?? '',
            10
          )}
        </Tooltip>
      ),
    },
    {
      field: 'transactionScheduleAt',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.DUE_DATE'),
      renderCell: ({ row }) => (
        <>
          {row.transactionRequest.dueDate
            ? DateTime.fromISO(row.transactionRequest.dueDate).toFormat(
                'dd/MM/yyyy',
                { locale: LANGUAGE }
              )
            : '-'}
        </>
      ),
    },
    {
      field: 'transactionScheduleAt',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.DUE_DATE'),
      renderCell: ({ row }) => (
        <>
          {row.transactionRequest.transactionScheduleAt ? (
            DateTime.fromISO(
              row.transactionRequest.transactionScheduleAt
            ).toFormat('dd/MM/yyyy', { locale: LANGUAGE })
          ) : (
            <Tooltip
              title={translate('PAGES.HOME.APPROVED.TABLE.AFTER_APPROVAL')}
            >
              {truncateTextWithEllipsis(
                translate('PAGES.HOME.APPROVED.TABLE.AFTER_APPROVAL'),
                15
              )}
            </Tooltip>
          )}
        </>
      ),
    },
    {
      field: 'totalValue',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TOTAL_VALUE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{moneyFormat(column.row.transactionRequest?.amount)}</>
      ),
    },
    {
      field: 'status',
      flex: 1,
      minWidth: 150,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.STATUS'),
      renderCell: (column: GridRenderCellParams) => (
        <div className="approved__approve-count">
          <div className="approved__approve-count__badge">
            {column.row.transactionApprovals?.length}/{column.row.minApprovals}
            <div className="approved__approve-count__description">
              {validationStatus(column.row.status)}
            </div>
          </div>
        </div>
      ),
    },
    {
      field: 'action',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.ACTIONS'),
      renderCell: ({ row }) => (
        <div className="approved__button-detail">
          <AdvancedDropdown
            testId="dropdown-icon"
            icon={<SettingOutlined />}
            items={[
              {
                key: 'details',
                icon: <EyeOutlined />,
                label: (
                  <button
                    className="transparent-button"
                    onClick={() => openTransactionDetailModal(row)}
                  >
                    {translate('PAGES.HOME.APPROVED.TABLE.COLUMN.DETAILS')}
                  </button>
                ),
              },
            ]}
            placement={'top'}
          />
        </div>
      ),
    },
  ];

  const columnsExecutable = [
    {
      field: 'id',
      flex: 1,
      headerName: 'id',
      sortable: false,
      hide: true,
    },
    {
      field: 'transaction',
      flex: 1,
      headerName: translate(
        'PAGES.HOME.APPROVED.TABLE.COLUMN.TYPE_TRANSACTION'
      ),
      renderCell: (column: GridRenderCellParams) => (
        <>{getTransactionName(column.row.transactionRequest?.type)}</>
      ),
    },
    {
      field: 'solicitation',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION'),
      renderCell: (column: GridRenderCellParams) => (
        <Tooltip title={column.row?.transactionRequest?.account?.name}>
          {truncateTextWithEllipsis(
            column.row.transactionRequest?.account?.name ?? '-',
            10,
            true
          )}
        </Tooltip>
      ),
    },
    {
      field: 'createdAt',
      flex: 1,
      headerName: translate(
        'PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION_DATE'
      ),
      renderCell: (columns: GridRenderCellParams) => (
        <>
          {DateTime.fromISO(columns.row.createdAt).toFormat(
            translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
          )}
        </>
      ),
    },
    {
      field: 'status',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.STATUS'),
      renderCell: ({ row }) => {
        const statusContent = (
          <>{renderStatusWithColor(row.transactionRequest.status)}</>
        );
        const message = row?.message || row?.transactionRequest?.message;

        if (message) {
          return <Tooltip title={message}>{statusContent}</Tooltip>;
        }

        return statusContent;
      },
    },
    {
      field: 'transactionScheduleAt',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.DUE_DATE'),
      renderCell: ({ row }) => (
        <>
          {row.transactionRequest.transactionScheduleAt
            ? DateTime.fromISO(
                row.transactionRequest.transactionScheduleAt
              ).toFormat('dd/MM/yyyy', { locale: LANGUAGE })
            : DateTime.fromISO(row.updatedAt).toFormat(
                translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
              )}
        </>
      ),
    },
    {
      field: 'totalValue',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TOTAL_VALUE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{moneyFormat(column.row.transactionRequest?.amount)}</>
      ),
    },
    {
      field: 'approvals',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.APPROVER'),
      renderCell: (column: GridRenderCellParams) => {
        const element = column.row.transactionApprovals.map(
          (item) => item.user?.name
        );

        return (
          <div className="scroll">
            {element.length > 0 ? element.join(', ') : '-'}
          </div>
        );
      },
    },
  ];

  const columnsReproved = [
    {
      field: 'id',
      flex: 1,
      headerName: 'id',
      sortable: false,
      hide: true,
    },
    {
      field: 'createdAt',
      flex: 2,
      headerName: translate(
        'PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION_DATE'
      ),
      renderCell: (columns: GridRenderCellParams) => (
        <>
          {DateTime.fromISO(columns.row.createdAt).toFormat(
            translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
          )}
        </>
      ),
    },
    {
      field: 'solicitation',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION'),
      renderCell: (column: GridRenderCellParams) => (
        <Tooltip title={column.row.transactionRequest?.account?.name}>
          {truncateTextWithEllipsis(
            column.row.transactionRequest?.account?.name ?? '-',
            10,
            true
          )}
        </Tooltip>
      ),
    },
    {
      field: 'transaction',
      flex: 2,
      headerName: translate(
        'PAGES.HOME.APPROVED.TABLE.COLUMN.TYPE_TRANSACTION'
      ),
      renderCell: (column: GridRenderCellParams) => (
        <>{getTransactionName(column.row.transactionRequest?.type)}</>
      ),
    },
    {
      field: 'reproveTransaction',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.REPROVE_DATE'),
      renderCell: (columns: GridRenderCellParams) => {
        const reproveDate = columns.row.transactionApprovals.filter(
          (item) => item.type === TransactionApprovalType.REPROVE
        );

        return (
          <>
            {reproveDate.length > 0 && reproveDate[0].createdAt
              ? DateTime.fromISO(reproveDate[0].createdAt).toFormat(
                  translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
                )
              : DateTime.fromISO(columns.row.updatedAt).toFormat(
                  translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
                )}
          </>
        );
      },
    },
    {
      field: 'totalValue',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TOTAL_VALUE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{moneyFormat(column.row.transactionRequest?.amount)}</>
      ),
    },
    {
      field: 'reprovals',
      flex: 1,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.REPROVAL'),
      renderCell: (column: GridRenderCellParams) => {
        const element = column.row.transactionApprovals
          .filter((item) => item.type === TransactionApprovalType.REPROVE)
          .map((item) => item.user?.name);

        return (
          <div className="scroll">
            {element.length > 0 ? element.join(', ') : '-'}
          </div>
        );
      },
    },
  ];

  const columnsResult = [
    {
      field: 'id',
      flex: 1,
      headerName: 'id',
      sortable: false,
      hide: true,
    },
    {
      field: 'solicitation',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION'),
      renderCell: (column: GridRenderCellParams) => (
        <>{column.row.transactionRequest?.account.name}</>
      ),
    },
    {
      field: 'totalValue',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TOTAL_VALUE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{moneyFormat(column.row.transactionRequest?.amount)}</>
      ),
    },
    {
      field: 'transaction',
      flex: 1.5,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TYPE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{getTransactionName(column.row.transactionRequest?.type)}</>
      ),
    },
    {
      field: 'message',
      flex: 5,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.REASON'),
      renderCell: (column: GridRenderCellParams) => <>{column.row.message}</>,
    },
  ];

  const columnsFailedResult = [
    {
      field: 'id',
      flex: 1,
      headerName: 'id',
      sortable: false,
      hide: true,
    },
    {
      field: 'solicitation',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.SOLICITATION'),
      renderCell: (column: GridRenderCellParams) => (
        <>{column.row.transactionRequest?.account.name}</>
      ),
    },
    {
      field: 'totalValue',
      flex: 2,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TOTAL_VALUE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{moneyFormat(column.row.transactionRequest?.amount)}</>
      ),
    },
    {
      field: 'transaction',
      flex: 1.5,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TYPE'),
      renderCell: (column: GridRenderCellParams) => (
        <>{getTransactionName(column.row.transactionRequest?.type)}</>
      ),
    },
    {
      field: 'reason',
      flex: 5,
      headerName: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.REASON'),
      renderCell: (column: GridRenderCellParams) => <>{column.row.reason}</>,
    },
  ];

  const renderDataTable = (
    columns: GridColDef[],
    rows: unknown[],
    rowCount: number,
    advancedFilters: advancedFilterModels.baseFilter,
    setAdvancedFilters: (filters: advancedFilterModels.baseFilter) => void,
    status: TransactionRequestPendingApprovalStatus
  ) => {
    const handleChange = (filters: advancedFilterModels.baseFilter) => {
      const searchFilters = { ...advancedFilters, ...filters, status };
      searchFilters.limit =
        searchFilters.pageSize !== undefined
          ? Number(searchFilters.pageSize)
          : 10;

      searchFilters.offset =
        filters.page !== undefined
          ? (Number(filters.page) - 1) * searchFilters.limit
          : 0;

      setAdvancedFilters(searchFilters);

      if (me.accountId) {
        onSearch(searchFilters);
      }
    };

    return (
      <DataTable
        columns={columns}
        rows={(rows ?? []) as { [key: string]: unknown }[]}
        rowCount={rowCount ?? 0}
        pageSize={advancedFilters.pageSize}
        orderBy={advancedFilters.orderBy}
        sort={advancedFilters.sort}
        checkboxSelection={
          status === TransactionRequestPendingApprovalStatus.PENDING
        }
        onSelectionModelChange={
          status === TransactionRequestPendingApprovalStatus.PENDING
            ? handleCheckboxChange
            : undefined
        }
        onChange={handleChange}
      />
    );
  };

  const renderApprovalButtons = () => {
    if (selectedRows.length === 0) return null;

    const alreadyApproved = selectedRows.some((row) =>
      row.transactionApprovals?.some((approval) => approval.userId === me.id)
    );

    if (alreadyApproved) {
      return (
        <Col>
          <Row className="approved__alreadyApproved">
            <ThemedText
              variant="body"
              className="approved_alreadyApproved__text"
            >
              <ThemedText fontWeight="bold">
                {translate('PAGES.HOME.APPROVED.VALIDATION.ALREADY_APPROVED') ||
                  translate(
                    'PAGES.HOME.APPROVED.VALIDATION.ALREADY_APPROVED_TWO'
                  )}
              </ThemedText>
              <br />
              {translate(
                'PAGES.HOME.APPROVED.VALIDATION.CANNOT_APPROVE_AGAIN'
              ) ||
                translate(
                  'PAGES.HOME.APPROVED.VALIDATION.CANNOT_REPROVE_AGAIN_TWO'
                )}
            </ThemedText>
          </Row>
        </Col>
      );
    }

    const totalSelectedAmount = selectedRows.reduce(
      (acc, row) => acc + row?.transactionRequest?.amount,
      0
    );

    const canApprove =
      balance && Number(balance.available) >= totalSelectedAmount;
    const hasRole =
      roles?.some(
        (item) =>
          item === RolesType.TRANSACTION_APPROVER ||
          item === RolesType.MY_ACCOUNT
      ) ?? false;

    return (
      <>
        <div className="approved__buttons-container">
          <AdvancedButton
            text={translate('PAGES.HOME.APPROVED.DETAILS.BUTTON_REPROVE')}
            variant="contained"
            customStyles={{ background: palette.error.main }}
            disableElevation
            hoverColor={palette.error.light}
            onClick={() => {
              setIsPinModalVisible(true);
              setStatus(TransactionApprovalType.REPROVE);
            }}
            disabled={!hasRole}
            size="medium"
          />
          <AdvancedButton
            text={translate('PAGES.HOME.APPROVED.DETAILS.BUTTON_APPROVE')}
            variant="contained"
            onClick={() => {
              setIsPinModalVisible(true);
              setStatus(TransactionApprovalType.APPROVE);
            }}
            disabled={!hasRole || !canApprove}
            size="medium"
          />
          <ThemedText variant="body" className="approved__total-amount">
            {translate('PAGES.HOME.APPROVED.TOTAL_AMOUNT')}{' '}
            {moneyFormat(totalSelectedAmount)}
          </ThemedText>
        </div>
      </>
    );
  };

  const handleChangeTab = (key: number) => {
    setTabKey(key);
    handleRefresh(key);
  };

  const switchList = () => {
    const tabOptions = [
      {
        title: translate('PAGES.HOME.APPROVED.SWITCH.PENDING_APPROVE'),
        key: TransactionRequestPendingApprovalStatus.PENDING,
        children: (
          <div>
            {renderApprovalButtons()}
            {renderDataTable(
              columnsPendingApprove,
              list ?? [],
              countPendingApproval ?? 0,
              advancedFiltersPendingApproval,
              setAdvancedFiltersPendingApproval,
              TransactionRequestPendingApprovalStatus.PENDING
            )}
          </div>
        ),
      },
      {
        title: translate('PAGES.HOME.APPROVED.SWITCH.APPROVE'),
        key: TransactionRequestPendingApprovalStatus.APPROVED,
        children: renderDataTable(
          columnsExecutable,
          listExecuted ?? [],
          countExecuted ?? 0,
          advancedFiltersExecuted,
          setAdvancedFiltersExecuted,
          TransactionRequestPendingApprovalStatus.APPROVED
        ),
      },
      {
        title: translate('PAGES.HOME.APPROVED.SWITCH.REPROVED'),
        key: TransactionRequestPendingApprovalStatus.REPROVED,
        children: renderDataTable(
          columnsReproved,
          transactionReproved ?? [],
          countReproved ?? 0,
          advancedFiltersReproved,
          setAdvancedFiltersReproved,
          TransactionRequestPendingApprovalStatus.REPROVED
        ),
      },
    ];

    return (
      <AdvancedCard testId="approved-card">
        {renderFilterAccordingToTab()}
        <Tabs
          defaultActiveKey={TransactionRequestPendingApprovalStatus.PENDING.toString()}
          activeKey={tabKey.toString()}
          type="card"
          onChange={(value) => handleChangeTab(Number(value))}
        >
          {tabOptions.map((tab) => (
            <Tabs.TabPane tab={tab.title} key={tab.key}>
              {tab.children}
            </Tabs.TabPane>
          ))}
        </Tabs>
      </AdvancedCard>
    );
  };

  useEffect(() => {
    const success = multipleApprovalResult?.success?.map((transaction) => {
      const successTransaction = cachedSelected.find(
        (cachedTransaction) => cachedTransaction.id === transaction.id
      );
      return {
        ...successTransaction,
        message: transaction.message,
      };
    }) as ApproveTransaction[];
    const failed = multipleApprovalResult?.error?.map((transaction) => {
      const failedTransaction = cachedSelected.find(
        (cachedTransaction) => cachedTransaction.id === transaction.id
      );
      return {
        ...failedTransaction,
        id: transaction.id || `failed-`,
        reason: transaction.message,
      };
    }) as FailedApproveTransaction[];
    setApprovalResult({ success, failed });
  }, [cachedSelected, multipleApprovalResult]);

  const onSearch = (
    filters: advancedFilterModels.ApprovalTransactionFilter
  ) => {
    const queryParams = queryStringToObject();
    filters.offset =
      filters.page !== undefined
        ? filters.page * (filters.pageSize ?? filters.limit ?? 10)
        : 0;

    const newFilter = {
      accountId: me.accountId,
      ...queryParams,
      ...filters,
      status: tabKey ?? TransactionRequestPendingApprovalStatus.PENDING,
    };

    setAdvancedFiltersPendingApproval(newFilter);
    if (me.accountId) {
      dispatch(getApprovalTransactions(newFilter));
    }
  };

  const handleStatusOptions = useMemo<models.SelectOptions[]>(() => {
    return Object.values(TransactionType)
      .filter((status) => typeof status === 'number')
      .map((status) => ({
        name: getTransactionName(status as number) ?? '',
        value: status as number,
      }))
      .filter((item) => item.name.trim() !== '' && item.value != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const renderFilterAccordingToTab = () => {
    const fields = [
      {
        name: 'destinationName',
        placeholder: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.RECEIVER'),
        type: AdvancedFilterType.TEXT,
        defaultValue: filterParams.get('destinationName') ?? '',
        hide: tabKey !== TabIndexEnum.PENDING_APPROVAL,
      },
      {
        name: 'transactionType',
        placeholder: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TYPE'),
        type: AdvancedFilterType.SELECT,
        options: handleStatusOptions,
        defaultValue: filterParams.get('transactionType')
          ? filterParams.get('transactionType')
          : '',
      },
      {
        name: 'amount',
        placeholder: translate('PAGES.HOME.APPROVED.TABLE.COLUMN.TOTAL_VALUE'),
        type: AdvancedFilterType.TEXT,
        formatter: (value) => maskMoney(value),
        defaultValue: filterParams.get('amount') ?? '',
        disableFormattingMoney: true,
      },
      {
        name: 'dateRange',
        placeholder: [
          translate('PAGES.CONDOMINIUMS.ADVANCED_FILTER.CREATED_AT_START'),
          translate('PAGES.CONDOMINIUMS.ADVANCED_FILTER.CREATED_AT_END'),
        ] as [string, string],
        type: AdvancedFilterType.RANGE_PICKER,
        defaultValue: filterParams.get('dateRange') ?? '',
        disableFuture: true,
        onlyDate: true,
        freeField: true,
      },
    ];

    return (
      <AdvancedModalFilters
        fields={fields.filter((field) => !field.hide)}
        onSearch={onSearch}
        refreshPage={() => handleRefresh(tabKey)}
      />
    );
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('APPLICATION.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.HOME.APPROVED.PAGE_TITLE'),
          },
        ]}
      />
      <div className="approved">
        <AdvancedPanelContentHeader
          title={translate('PAGES.HOME.APPROVED.PAGE_TITLE')}
          testId="approved-header"
          description={translate('PAGES.HOME.APPROVED.PAGE_DESCRIPTION')}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
        />
        {switchList()}
        <ConfirmationModal
          visible={isPinModalVisible}
          setVisible={setIsPinModalVisible}
          handleConfirmation={handleConfirmation}
          text={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TEXT')}
          title={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TITLE')}
        />
        <ApprovedTransactionDetailModal
          visible={transactionDetailModalVisible}
          setVisible={setTransactionDetailModalVisible}
          data={transactionDetail}
        />
        <Modal
          centered
          open={modalIsVisible}
          closable
          footer={null}
          onOk={() => setModalIsVisible(false)}
          onCancel={() => setModalIsVisible(false)}
          width={'80%'}
        >
          {approvalResult?.failed && approvalResult?.failed.length > 0 ? (
            <>
              <ThemedText variant="h4">
                {status === TransactionApprovalType.APPROVE
                  ? translate('PAGES.HOME.APPROVED.FAILED_TRANSACTION')
                  : translate('PAGES.HOME.APPROVED.FAILED_REPROVED')}
              </ThemedText>
              <DataTable
                columns={columnsFailedResult}
                rows={approvalResult?.failed ?? []}
                pageSize={30}
                orderBy={''}
                sort={'desc'}
              />
            </>
          ) : (
            <></>
          )}
          {approvalResult?.success && approvalResult?.success.length > 0 ? (
            <>
              <ThemedText variant="h4">
                {status === TransactionApprovalType.APPROVE
                  ? translate('PAGES.HOME.APPROVED.SUCCESS_TRANSACTION')
                  : translate('PAGES.HOME.APPROVED.REPROVED_TRANSACTION')}
              </ThemedText>
              <DataTable
                columns={columnsResult}
                rows={approvalResult?.success ?? []}
                pageSize={30}
                orderBy={''}
                sort={'desc'}
              />
            </>
          ) : (
            <></>
          )}
        </Modal>
      </div>
    </>
  );
};

export default Approved;
